import { ArrowRightOutlined, SettingOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { useAuth } from "@ap/ui-kit";
import { Modal } from "antd";
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { filterPalette, paletteItems, type PaletteContext, type PaletteItem } from "../lib/palette";

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  ctx: PaletteContext;
}

/**
 * Command palette (Ctrl/⌘+K, ADR-0055): an ARIA combobox over a listbox of the pages the user may
 * open and the quick actions their roles allow.
 */
export function CommandPalette({ open, onClose, ctx }: CommandPaletteProps) {
  const { t } = useTranslation("palette");
  const { t: tAny } = useTranslation();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  const all = useMemo(() => paletteItems(user?.roles ?? [], (k, o) => tAny(k, o)), [user, tAny]);
  const items = useMemo(() => filterPalette(all, q), [all, q]);
  const pages = items.filter((i) => i.kind === "page");
  // Settings show once the person types: a short list of pages and actions stays the default.
  const settings = q.trim() ? items.filter((i) => i.kind === "setting") : [];
  const actions = items.filter((i) => i.kind === "action");
  const ordered = [...pages, ...settings, ...actions];

  useEffect(() => {
    if (!open) return;
    setQ("");
    setActive(0);
    const id = requestAnimationFrame(() => input.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);
  useEffect(() => setActive(0), [q]);

  const choose = (item: PaletteItem | undefined) => {
    if (!item) return;
    onClose();
    if (item.path) ctx.navigate(item.path);
    item.run?.(ctx);
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => (ordered.length ? (a + 1) % ordered.length : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => (ordered.length ? (a - 1 + ordered.length) % ordered.length : 0));
    } else if (e.key === "Home") {
      setActive(0);
    } else if (e.key === "End") {
      setActive(Math.max(0, ordered.length - 1));
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(ordered[active]);
    }
  };

  const optId = (i: number) => `${listId}-opt-${i}`;
  const renderGroup = (label: string, list: PaletteItem[], offset: number) =>
    list.length > 0 ? (
      <li role="presentation" className="ap-pal-group">
        <div className="ap-pal-group-label" id={`${listId}-${label}`} aria-hidden>
          {label}
        </div>
        <ul role="group" aria-labelledby={`${listId}-${label}`} className="ap-pal-options">
          {list.map((item, k) => {
            const i = offset + k;
            return (
              // Mouse users click; keyboard users use the combobox (aria-activedescendant).
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <li
                key={item.id}
                id={optId(i)}
                role="option"
                aria-selected={i === active}
                className="ap-pal-option"
                data-active={i === active ? "true" : undefined}
                data-testid={`palette-${item.id}`}
                onMouseMove={() => setActive(i)}
                onClick={() => choose(item)}
              >
                <span className="ap-pal-icon" aria-hidden>
                  {item.kind === "page" ? <ArrowRightOutlined /> : item.kind === "setting" ? <SettingOutlined /> : <ThunderboltOutlined />}
                </span>
                <span className="ap-pal-label">{item.label}</span>
                {item.hint ? <span className="ap-pal-hint">{item.hint}</span> : null}
              </li>
            );
          })}
        </ul>
      </li>
    ) : null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={600}
      title={<span className="ap-visually-hidden">{t("title")}</span>}
      className="ap-palette"
      afterOpenChange={(o) => o && input.current?.focus()}
      destroyOnHidden
      styles={{ body: { padding: 0 } }}
      style={{ insetBlockStart: 96 }}
    >
      <div className="ap-pal">
        <input
          ref={input}
          className="ap-pal-input"
          role="combobox"
          aria-expanded={ordered.length > 0}
          aria-controls={ordered.length > 0 ? listId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={ordered.length ? optId(active) : undefined}
          aria-label={t("title")}
          placeholder={t("placeholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          data-testid="palette-input"
        />
        {ordered.length > 0 ? (
          <ul id={listId} role="listbox" aria-label={t("title")} className="ap-pal-list">
            {renderGroup(t("pages"), pages, 0)}
            {renderGroup(t("settings"), settings, pages.length)}
            {renderGroup(t("actions"), actions, pages.length + settings.length)}
          </ul>
        ) : (
          <p className="ap-pal-empty" role="status">
            {t("empty")}
          </p>
        )}
        <p className="ap-pal-hint-bar" aria-hidden>
          {t("hint")}
        </p>
      </div>
    </Modal>
  );
}
